import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnFiltersState,
} from "@tanstack/react-table"
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  FileText,
  Filter,
  X,
  Info,
  Calendar,
  Files,
} from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/Input"
import { Select, SelectTrigger, SelectValue, SelectPopup, SelectItem } from "@/components/ui/Select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { PreviewCard, PreviewCardTrigger, PreviewCardPopup } from "@/components/ui/preview-card"
import { Card, CardHeader, CardPanel } from "@/components/ui/card"
import { PATH_ROOT } from "@/routes/paths"
import { useDealPipelineData } from "@/hooks/deals/useDealPipelineData"
import { formatCurrency } from "@/views/deals/utils"
import type { Deal, PipelineStage } from "@/types/deal.types"

interface DealsTableViewProps {
  onDealClick?: (dealId: string) => void
}

type DealStatus = "In Progress" | "Pending" | "Draft" | "Completed"

interface TableDeal {
  id: string
  name: string
  type: string
  status: DealStatus
  value: string
  date: string
  updatedAt: string
  documentCount: number
  description: string
}

const STAGE_STATUS_MAP: Record<PipelineStage, DealStatus> = {
  lead: "Pending",
  in_progress: "In Progress",
  signing: "Draft",
  delivery: "Draft",
  complete: "Completed",
  won: "Completed",
  lost: "Draft",
  closed: "Completed",
}

const STATUS_ITEMS = [
  { value: "all", label: "All Statuses" },
  { value: "In Progress", label: "In Progress" },
  { value: "Pending", label: "Pending" },
  { value: "Draft", label: "Draft" },
  { value: "Completed", label: "Completed" },
]

const columnHelper = createColumnHelper<TableDeal>()

const getStatusVariant = (status: TableDeal["status"]) => {
  switch (status) {
    case "Completed":
      return "success"
    case "In Progress":
      return "info"
    case "Pending":
      return "warning"
    case "Draft":
      return "secondary"
    default:
      return "secondary"
  }
}

function mapDealToTableRow(deal: Deal): TableDeal {
  return {
    id: deal.id,
    name: deal.name,
    type: deal.type,
    status: STAGE_STATUS_MAP[deal.stage],
    value: formatCurrency(deal.value),
    date: deal.date,
    updatedAt: deal.date,
    documentCount: deal.documentsCount,
    description: deal.description,
  }
}

export function DealsTableView({ onDealClick }: DealsTableViewProps) {
  const navigate = useNavigate()
  const isDemo = useIsDemo()
  const { initialDeals } = useDealPipelineData()
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<string | null>(null)

  const tableData = useMemo(() => initialDeals.map(mapDealToTableRow), [initialDeals])

  const typeItems = useMemo(
    () => [
      { value: "all", label: "All Types" },
      ...Array.from(new Set(tableData.map((deal) => deal.type))).map((type) => ({
        value: type,
        label: type,
      })),
    ],
    [tableData],
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Deal Name
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="size-3.5" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="size-3.5" />
            ) : (
              <ArrowUpDown className="size-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 group/name">
            <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText className="size-4 text-primary" />
            </div>
            <span className="font-medium text-foreground">{row.getValue("name")}</span>
            <PreviewCard>
              <PreviewCardTrigger
                className="opacity-0 group-hover/name:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="size-4 text-muted-foreground hover:text-foreground cursor-help" />
              </PreviewCardTrigger>
              <PreviewCardPopup className="w-80">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-semibold text-foreground">{row.original.name}</h4>
                      <p className="text-xs text-muted-foreground">{row.original.type}</p>
                    </div>
                    <Badge variant={getStatusVariant(row.original.status)}>{row.original.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{row.original.description}</p>
                  <div className="flex items-center gap-4 text-xs pt-2 border-t border-border">
                    <div className="flex items-center gap-1.5">
                      <span className="text-emerald-500 font-semibold">{row.original.value}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Files className="size-3.5" />
                      <span>{row.original.documentCount} docs</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Calendar className="size-3.5" />
                      <span>{row.original.updatedAt}</span>
                    </div>
                  </div>
                </div>
              </PreviewCardPopup>
            </PreviewCard>
          </div>
        ),
      }),
      columnHelper.accessor("type", {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Type
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="size-3.5" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="size-3.5" />
            ) : (
              <ArrowUpDown className="size-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => <span className="text-muted-foreground">{row.getValue("type")}</span>,
        filterFn: (row, id, value) => {
          if (!value) return true
          return row.getValue(id) === value
        },
      }),
      columnHelper.accessor("status", {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Status
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="size-3.5" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="size-3.5" />
            ) : (
              <ArrowUpDown className="size-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => <Badge variant={getStatusVariant(row.getValue("status"))}>{row.getValue("status")}</Badge>,
        filterFn: (row, id, value) => {
          if (!value) return true
          return row.getValue(id) === value
        },
      }),
      columnHelper.accessor("value", {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Value
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="size-3.5" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="size-3.5" />
            ) : (
              <ArrowUpDown className="size-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => <span className="font-medium tabular-nums">{row.getValue("value")}</span>,
        sortingFn: (rowA, rowB) => {
          const a = parseFloat((rowA.getValue("value") as string).replace(/[$,KM]/g, ""))
          const b = parseFloat((rowB.getValue("value") as string).replace(/[$,KM]/g, ""))
          return a - b
        },
      }),
      columnHelper.accessor("documentCount", {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Documents
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="size-3.5" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="size-3.5" />
            ) : (
              <ArrowUpDown className="size-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => <span className="text-muted-foreground tabular-nums">{row.getValue("documentCount")}</span>,
      }),
      columnHelper.accessor("updatedAt", {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Updated
            {column.getIsSorted() === "asc" ? (
              <ArrowUp className="size-3.5" />
            ) : column.getIsSorted() === "desc" ? (
              <ArrowDown className="size-3.5" />
            ) : (
              <ArrowUpDown className="size-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => <span className="text-muted-foreground text-sm">{row.getValue("updatedAt")}</span>,
      }),
    ],
    [],
  )

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  const handleRowClick = (dealId: string) => {
    if (onDealClick) {
      onDealClick(dealId)
    } else {
      navigate(`${isDemo ? PATH_ROOT.demo.deal : PATH_ROOT.main.deal}/${dealId}`)
    }
  }

  const handleStatusFilter = (status: string | null) => {
    setStatusFilter(status)
    if (status) {
      table.getColumn("status")?.setFilterValue(status)
    } else {
      table.getColumn("status")?.setFilterValue(undefined)
    }
  }

  const handleTypeFilter = (type: string | null) => {
    setTypeFilter(type)
    if (type) {
      table.getColumn("type")?.setFilterValue(type)
    } else {
      table.getColumn("type")?.setFilterValue(undefined)
    }
  }

  const clearFilters = () => {
    setGlobalFilter("")
    setStatusFilter(null)
    setTypeFilter(null)
    table.resetColumnFilters()
  }

  const hasActiveFilters = Boolean(globalFilter || statusFilter || typeFilter)

  return (
    <div className="p-4">
      <Card className="flex flex-col h-full gap-0">
        <CardHeader className="flex flex-row items-center gap-3 py-3 px-4 h-auto">
          <div className="relative flex-1 max-w-sm">
            <Input
              startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
              type="search"
              placeholder="Search deals..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              aria-label="Search deals"
            />
          </div>

          <Select
            items={STATUS_ITEMS}
            value={statusFilter ?? "all"}
            onValueChange={(v) => handleStatusFilter(v === "all" ? null : v)}
          >
            <SelectTrigger size="sm" className="w-[150px]" aria-label="Filter by status">
              <Filter className="size-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectPopup>
              {STATUS_ITEMS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>

          <Select
            items={typeItems}
            value={typeFilter ?? "all"}
            onValueChange={(v) => handleTypeFilter(v === "all" ? null : v)}
          >
            <SelectTrigger size="sm" className="w-[170px]" aria-label="Filter by type">
              <SelectValue />
            </SelectTrigger>
            <SelectPopup>
              {typeItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="size-4 mr-1" />
              Clear
            </Button>
          )}

          <span className="text-sm text-muted-foreground ml-auto">{table.getFilteredRowModel().rows.length} deals</span>
        </CardHeader>

        <CardPanel className="flex-1 overflow-auto px-0">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="text-muted-foreground">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => handleRowClick(row.original.id)}
                    className="cursor-pointer"
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center text-muted-foreground">
                      <FileText className="size-8 mb-2 opacity-50" />
                      <p>No deals found.</p>
                      {hasActiveFilters && (
                        <Button variant="link" size="sm" onClick={clearFilters} className="mt-1">
                          Clear filters
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardPanel>
      </Card>
    </div>
  )
}

export default DealsTableView
